// Import required libraries
import imagesLoaded from 'imagesloaded';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { carouselUtils } from './js/carousel';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

// Global variables
const body = document.body;
const pageLoader = document.querySelector('.site-mask');
let lastScrollTop = 0;

// Custom event for page ready
const pageReadyEvent = new Event('pageReady');

// Utility functions

// Check if a URL is external
const isExternalUrl = (url) => {
  if (!url || url.startsWith('/') || url.startsWith('#')) return false;
  try {
    const { hostname } = new URL(url);
    return hostname !== window.location.hostname;
  } catch (e) {
    console.warn(`Invalid URL: ${url}`);
    return false;
  }
};

// Add 'is-external' class to external links
const addExternalLinkClass = (link) => {
  if (isExternalUrl(link.href) && !link.classList.contains('is-external')) {
    link.classList.add('is-external');
  }
};

// Load utilities
const loadUtils = () => {
  return new Promise((resolve) => {
    imagesLoaded(body, () => {
      resolve();
    });
  });
};

// Body utilities
const bodyUtils = () => {
  const selectors = [
    '.mega-menu-link',
    '.wp-block-navigation__container a',
    '.entry-content p a, .entry-content li a',
    '.wp-block-team-member-bio p a, .wp-block-team-member-bio li a',
  ];

  for (const selector of selectors) {
    Array.from(document.querySelectorAll(selector)).forEach(addExternalLinkClass);
  }

  Array.from(document.querySelectorAll('.is-style-link:not(:has(*)), .is-style-link:has(.screen-reader-text), .wp-block-button.is-style-link .wp-block-button__link')).forEach(el => {
    el.classList.add('with-arrow');
  });

  document.querySelectorAll('.entry-content p, .entry-content li').forEach(function(element) {
    // Get the current color of the element
    const currentColor = window.getComputedStyle(element).color;

    // Check if the color is already rgba, if not convert it to rgba with 0.85 opacity
    const rgbaColor = currentColor.replace(/^rgb/, 'rgba').replace(')', ', 0.7)');

    // Apply the new color with opacity
    element.style.color = rgbaColor;

    // Find any <strong> or <b> elements within this element and reset their opacity
    element.querySelectorAll('strong, b').forEach(function(childElement) {
      childElement.style.color = currentColor;
    });
  });
};

const gsapPremiumUtils = () => {

};

// Window scroll utilities
const windowScrollUtils = () => {
  const setInitialScrollStatus = () => {
    const st = window.pageYOffset || document.documentElement.scrollTop;

    if (st < 50) {
      body.classList.add('header-top');
      body.classList.remove('header-sticky', 'header-hidden');
    } else {
      body.classList.remove('header-top');
      body.classList.add('header-sticky');
    }

    lastScrollTop = st;
  };

  const scrollHandler = () => {
    const st = window.pageYOffset || document.documentElement.scrollTop;
    const scrollThreshold = 20;

    if (st < ( lastScrollTop - scrollThreshold )) {
      body.classList.remove('header-hidden');
      body.classList.add('header-sticky');
    } else if (st > lastScrollTop) {
      body.classList.add('header-hidden');
      body.classList.remove('header-sticky');
    }

    if (st < 50) {
      body.classList.add('header-top');
      body.classList.remove('header-sticky', 'header-hidden');
    } else {
      body.classList.remove('header-top');
    }

    lastScrollTop = st;
  };

  // Set initial status when the page loads
  setInitialScrollStatus();

  // Add scroll event listener
  window.addEventListener('scroll', scrollHandler, { passive: true });

  // Optional: Update status on window resize
  window.addEventListener('resize', setInitialScrollStatus, { passive: true });
};

// Parallax effect for cover blocks
const parallaxUtils = () => {
  const parallaxCovers = document.querySelectorAll('.wp-block-cover.has-parallax');

  parallaxCovers.forEach((cover) => {
    const image = cover.querySelector('.wp-block-cover__image-background');

    if (image) {
      const isFixedBackground = window.getComputedStyle(image).backgroundAttachment === 'fixed';

      gsap.to(image, {
        backgroundPosition: isFixedBackground ? '50% 100%' : '50% 50%',
        ease: 'none',
        scrollTrigger: {
          trigger: cover,
          start: isFixedBackground ? 'top bottom' : 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    }
  });
};

export const formUtils = () => {
  const handleFieldInteraction = (field, action) => {
    const fieldWrapper = field.closest('.gfield');
    const label = fieldWrapper?.querySelector('.gfield_label, .gform-field-label');
    if (!label) return;

    const isEmpty = field.value?.trim() === '' || field.value === '(___) ___-____';
    const shouldActivate = action === 'focus' || !isEmpty;

    // Toggle 'active' class based on field state
    label.classList.toggle('active', shouldActivate);

    // Handle focus state separately
    label.classList.toggle('focused', action === 'focus');

    if (field.tagName.toLowerCase() === 'select') {
      label.classList.add('for-select');
    }
  };

  const setupField = (field) => {
    // Initial setup
    handleFieldInteraction(field, 'init');

    field.addEventListener('focus', () => handleFieldInteraction(field, 'focus'));
    field.addEventListener('blur', () => handleFieldInteraction(field, 'blur'));
    field.addEventListener('input', () => handleFieldInteraction(field, 'input'));
  };

  const setupFormFields = () => {
    Array.from(document.querySelectorAll('.gfield input, .gfield textarea, .gfield select')).forEach(setupField);
  };

  setupFormFields();
  return setupFormFields;
};

const marqueeUtils = () => {
  const marquees = document.querySelectorAll('.is-marquee');

  marquees.forEach((marquee) => {
    const marqueeContent = document.createElement('div');
    marqueeContent.classList.add('marquee__content');

    const wrapMarqueeContent = () => {
      while (marquee.firstChild) {
        marqueeContent.appendChild(marquee.firstChild);
      }
      marquee.appendChild(marqueeContent);
    };

    const cloneMarqueeContent = () => {
      const clone = marqueeContent.cloneNode(true);
      marquee.appendChild(clone);
    };

    const calculateMarqueeWidth = () => {
      return marqueeContent.getBoundingClientRect().width;
    };

    const startMarqueeAnimation = () => {
      const marqueeWidth = calculateMarqueeWidth();
      marquee.style.width = `${marqueeWidth * 2}px`;
    };

    wrapMarqueeContent();
    cloneMarqueeContent();
    startMarqueeAnimation();
  });
};

const testimonialSwitcherUtils = () => {
  const testimonialWrapper = document.querySelector('.testimonials');
  if (!testimonialWrapper) return;

  const testimonials = testimonialWrapper.querySelectorAll('.wp-block-pullquote');
  if (testimonials.length <= 1) return;

  let currentIndex = 0;

  testimonialWrapper.classList.add('js-ready');

  // Create and append navigation elements
  const nav = document.createElement('div');
  nav.className = 'testimonial-nav';
  nav.innerHTML = `
    <button class="prev-btn">
      <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19 21" width="19" height="21">
        <polygon points="0 10.7727 14 21.5455 14 0 0 10.7727" fill="currentColor" stroke-width="0"/>
      </svg>
    </button>
    <span class="testimonial-counter">1 / ${testimonials.length}</span>
    <button class="next-btn">
      <svg id="a" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19 21" width="19" height="21">
        <polygon points="5.2296 0 5.2296 21.5455 19.2296 10.7727 5.2296 0" fill="currentColor" stroke-width="0"/>
      </svg>
    </button>
  `;
  testimonialWrapper.appendChild(nav);

  const prevBtn = nav.querySelector('.prev-btn');
  const nextBtn = nav.querySelector('.next-btn');
  const counter = nav.querySelector('.testimonial-counter');

  // Set initial styles for all testimonials
  testimonials.forEach((testimonial, index) => {
    gsap.set(testimonial, {
      opacity: index === 0 ? 1 : 0,
      zIndex: index === 0 ? 2 : 1,
    });
  });

  const updateTestimonial = (newIndex) => {
    const oldTestimonial = testimonials[currentIndex];
    const newTestimonial = testimonials[newIndex];

    // Crossfade animation
    gsap.to(oldTestimonial, {
      opacity: 0,
      duration: 0.5,
      zIndex: 1,
    });

    gsap.to(newTestimonial, {
      opacity: 1,
      duration: 0.5,
      zIndex: 2,
    });

    currentIndex = newIndex;
    counter.textContent = `${currentIndex + 1} / ${testimonials.length}`;
  };

  const nextTestimonial = () => {
    const newIndex = ( currentIndex + 1 ) % testimonials.length;
    updateTestimonial(newIndex);
  };

  const prevTestimonial = () => {
    const newIndex = ( currentIndex - 1 + testimonials.length ) % testimonials.length;
    updateTestimonial(newIndex);
  };

  nextBtn.addEventListener('click', nextTestimonial);
  prevBtn.addEventListener('click', prevTestimonial);
};

// Function to check if all key events have fired
const checkPageReady = () => {
  // Perform your checks here, e.g., check if all necessary elements are loaded
  const isReady = true; // Replace with your actual condition

  if (isReady) {
    setTimeout(() => {
      document.dispatchEvent(pageReadyEvent);
    }, 300);
  }
};

// Function to handle link clicks
const handleLinkClick = (event) => {
  const link = event.target.closest('a');
  if (link && !isExternalUrl(link.href)) {
    body.classList.remove('is-loaded');
    if (pageLoader) {
      pageLoader.classList.remove('is-hidden');
    }
  }
};

// Add click event listener to the document
document.addEventListener('click', handleLinkClick);

// Initialize utilities on window load
window.addEventListener('load', async function() {
  await Promise.all([
    loadUtils(),
    bodyUtils(),
    gsapPremiumUtils(),
    windowScrollUtils(),
    formUtils(),
    parallaxUtils(),
    marqueeUtils(),
    testimonialSwitcherUtils(),
    carouselUtils(),
  ]);
  checkPageReady();
});

// Listen for the custom page ready event
document.addEventListener('pageReady', () => {
  body.classList.add('is-loaded');
  if (pageLoader) {
    pageLoader.classList.add('is-hidden');
  }
  body.dispatchEvent(new Event('loaded'));
});
